import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, List, ListItem, ListItemButton, ListItemText, Paper, Divider, Chip, Alert, Collapse, TextField, InputAdornment, IconButton, Button, Snackbar } from '@mui/material';
import { ExpandMore, ChevronRight, Folder, FolderOpen, Search as SearchIcon, Clear as ClearIcon, Link as LinkIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { getDocumentationFiles, getDocumentContent } from '../services/documentationService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DocumentItem } from '../types/documentation';

export const DocumentationViewer = () => {
  console.log('DocumentationViewer mounting...');
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState<DocumentItem[]>([]);
  const [selectedFile, setSelectedFile] = useState<DocumentItem | null>(null);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  useEffect(() => {
    const loadFiles = async () => {
      setLoading(true);
      try {
        const docs = await getDocumentationFiles();
        setFiles(docs);
        
        const docParam = searchParams.get('doc');
        if (docParam) {
          const fileToSelect = docs.find(d => d.filename === docParam);
          if (fileToSelect) {
            setSelectedFile(fileToSelect);
          } else {
            // Fallback: allow direct load when /md/list hasn't included the file yet
            setSelectedFile({
              id: docParam.replace(/\.md$/i, ''),
              title: docParam.replace('.md', '').replace(/-/g, ' '),
              filename: docParam,
              description: `Documentation file: ${docParam}`,
              category: 'General',
              size: 0
            });
          }
        }
      } catch (error) {
        console.error('Error loading documentation:', error);
      } finally {
        setLoading(false);
        console.log('Loading complete');
      }
    };
    loadFiles();
  }, [searchParams]);

  useEffect(() => {
    if (selectedFile) {
      const loadContent = async () => {
        setLoading(true);
        try {
          const text = await getDocumentContent(selectedFile.filename);
          console.log('Loaded content:', text); // Added debug logging
          setContent(text);
        } finally {
          setLoading(false);
        }
      };
      loadContent();
    }
  }, [selectedFile]);

  // Filter files based on search query
  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) {
      return files;
    }
    const query = searchQuery.toLowerCase();
    return files.filter(file => 
      file.title.toLowerCase().includes(query) ||
      file.filename.toLowerCase().includes(query) ||
      file.category.toLowerCase().includes(query)
    );
  }, [files, searchQuery]);

  // Group files by category
  const groupedFiles = filteredFiles.reduce((acc, file) => {
    if (!acc[file.category]) {
      acc[file.category] = [];
    }
    acc[file.category].push(file);
    return acc;
  }, {} as Record<string, DocumentItem[]>);

  const categories = Object.keys(groupedFiles).sort();

  // Auto-expand categories when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      const expanded: Record<string, boolean> = {};
      categories.forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
    }
  }, [searchQuery, categories]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleFileSelect = (file: DocumentItem) => {
    setSelectedFile(file);
    // Update URL with doc parameter
    setSearchParams({ doc: file.filename });
  };

  const copyDocLink = () => {
    if (selectedFile) {
      const url = `${window.location.origin}/docs?doc=${encodeURIComponent(selectedFile.filename)}`;
      navigator.clipboard.writeText(url).then(() => {
        setCopySuccess(true);
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <Paper sx={{ width: 300, p: 2, overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          Documentation Files
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          {files.length} files in {categories.length} categories
        </Typography>
        
        {/* Search Input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search files..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  sx={{ 
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 51, 102, 0.08)'
                    }
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
          aria-label="Search documentation files by title, filename, or category"
        />

        {/* Search Results Count */}
        {searchQuery && (
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ mb: 1, display: 'block', fontStyle: 'italic' }}
          >
            {filteredFiles.length} result{filteredFiles.length !== 1 ? 's' : ''} found
          </Typography>
        )}

        {/* No Results Message */}
        {searchQuery && filteredFiles.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No files found matching "{searchQuery}"
          </Alert>
        )}
        
        {categories.map((category) => {
          const isExpanded = expandedCategories[category] || false;
          const fileCount = groupedFiles[category].length;
          
          return (
            <Box key={category} sx={{ mb: 1 }}>
              <ListItemButton 
                onClick={() => toggleCategory(category)}
                sx={{ 
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 51, 102, 0.08)'
                  }
                }}
              >
                {isExpanded ? (
                  <ExpandMore sx={{ fontSize: '1.2rem', color: 'primary.main', mr: 1 }} />
                ) : (
                  <ChevronRight sx={{ fontSize: '1.2rem', color: 'primary.main', mr: 1 }} />
                )}
                {isExpanded ? (
                  <FolderOpen sx={{ fontSize: '1.2rem', color: 'primary.main', mr: 1 }} />
                ) : (
                  <Folder sx={{ fontSize: '1.2rem', color: 'primary.main', mr: 1 }} />
                )}
                <ListItemText 
                  primary={category}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    color: '#003366'
                  }}
                />
                <Chip 
                  label={fileCount} 
                  size="small" 
                  sx={{ 
                    height: 20,
                    minWidth: 20,
                    fontSize: '0.7rem',
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }} 
                />
              </ListItemButton>
              
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List dense sx={{ pl: 4 }}>
                  {groupedFiles[category].map((file) => (
                    <ListItem key={file.id} disablePadding>
                      <ListItemButton 
                        selected={selectedFile?.id === file.id}
                        onClick={() => handleFileSelect(file)}
                        sx={{ 
                          py: 0.5,
                          borderRadius: 1
                        }}
                      >
                        <ListItemText 
                          primary={file.title}
                          primaryTypographyProps={{
                            fontSize: '0.75rem',
                            sx: { 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Paper>
      
      <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Copy Link Button - Show when file is selected */}
        {selectedFile && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              <strong>{selectedFile.title}</strong>
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={copyDocLink}
              sx={{
                color: 'primary.main',
                borderColor: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(0, 51, 102, 0.08)'
                }
              }}
              aria-label="Copy shareable link to this document"
            >
              Copy Link
            </Button>
          </Box>
        )}
        
        {loading ? (
          <Typography>Loading...</Typography>
        ) : content ? (
          <Box
            sx={{
              '& h1': {
                color: 'primary.main',
                fontSize: '2rem',
                fontWeight: 'bold',
                mt: 3,
                mb: 2,
                borderBottom: '2px solid #003366',
                pb: 1
              },
              '& h2': {
                color: 'primary.main',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                mt: 3,
                mb: 2
              },
              '& h3': {
                color: 'primary.main',
                fontSize: '1.25rem',
                fontWeight: 'bold',
                mt: 2,
                mb: 1
              },
              '& p': {
                lineHeight: 1.8,
                mb: 2
              },
              '& ul, & ol': {
                pl: 3,
                mb: 2
              },
              '& li': {
                mb: 1
              },
              '& code': {
                backgroundColor: '#f5f5f5',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.9em',
                fontFamily: 'monospace'
              },
              '& pre': {
                backgroundColor: '#f5f5f5',
                padding: '16px',
                borderRadius: '8px',
                overflow: 'auto',
                mb: 2
              },
              '& pre code': {
                backgroundColor: 'transparent',
                padding: 0
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 3,
                border: '1px solid #e0e0e0'
              },
              '& th': {
                backgroundColor: 'primary.main',
                color: 'white',
                padding: '12px',
                textAlign: 'left',
                fontWeight: 'bold'
              },
              '& td': {
                padding: '12px',
                borderBottom: '1px solid #e0e0e0'
              },
              '& a': {
                color: '#2196f3',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              minHeight: '400px',
              textAlign: 'left',
              p: 4
            }}
            role="region"
            aria-label="Documentation Center Welcome"
          >
            <Box
              sx={{
                mb: 3,
                p: 3,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 51, 102, 0.05)',
                width: '100%'
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                  mb: 2,
                  fontFamily: 'Merriweather, serif'
                }}
              >
                📚 Documentation Center
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  color: 'primary.main',
                  mb: 3,
                  fontWeight: 600
                }}
              >
                Welcome to E-Unify Documentation Files
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                  lineHeight: 1.8
                }}
              >
                Browse through our comprehensive documentation library organized by category. 
                Use the search box above to quickly find specific files, or expand categories 
                to explore available documentation.
              </Typography>
              
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: 'white',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 'bold',
                    mb: 1
                  }}
                >
                  How to Get Started:
                </Typography>
                <Box
                  component="ol"
                  sx={{
                    textAlign: 'left',
                    pl: 3,
                    m: 0,
                    '& li': {
                      mb: 1,
                      color: 'text.secondary'
                    }
                  }}
                >
                  <li>
                    <strong>Search:</strong> Type keywords in the search box to filter files
                  </li>
                  <li>
                    <strong>Browse:</strong> Click on category folders to expand and view files
                  </li>
                  <li>
                    <strong>Select:</strong> Click any file name to view its content here
                  </li>
                  <li>
                    <strong>Navigate:</strong> Use keyboard (Tab, Enter, Arrow keys) for accessibility
                  </li>
                </Box>
              </Box>
              
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 3,
                  color: 'text.secondary',
                  fontStyle: 'italic'
                }}
              >
                💡 Tip: Try searching for "LOE", "PDCA", or "Planning" to find relevant documentation
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      
      {/* Success notification for copied link */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="📋 Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#4caf50',
            color: 'white',
            fontWeight: 'bold'
          }
        }}
      />
    </Box>
  );
};
